"use client";

import { useState } from "react";
import { LuArrowRight } from "react-icons/lu";

import { DATA_CATEGORIES } from "@/app/[tenant]/(console)/clinic-setup/import-sources";

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { toast } from "@/lib/use-toast";

import { reportDataIssue } from "@/api/clinic-setup";

interface ReportDataIssueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category: string;
}

export function ReportDataIssueDialog({
  open,
  onOpenChange,
  category,
}: ReportDataIssueDialogProps) {
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const categoryLabel =
    DATA_CATEGORIES.find((c) => c.value === category)?.label ?? category;

  const handleSubmit = async () => {
    if (!description.trim()) return;
    setSubmitting(true);
    try {
      await reportDataIssue({
        category,
        description: description.trim(),
        pageUrl: window.location.href,
      });
      toast({
        title: "Issue reported",
        description: "Our team will look into this.",
        variant: "success",
      });
      setDescription("");
      onOpenChange(false);
    } catch {
      toast({
        title: "Failed to report issue",
        description: "Please try again.",
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Report a data issue</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Category</Label>
              <div className="rounded-lg border border-border bg-background-secondary px-3 py-2 text-body text-foreground-secondary">
                {categoryLabel}
              </div>
            </div>
            <div className="space-y-1">
              <Label mandatory>What looks wrong?</Label>
              <Textarea
                placeholder="Describe the issue you noticed..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
              />
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <button
            onClick={() => onOpenChange(false)}
            className="text-body-strong text-foreground-secondary transition-colors hover:text-foreground"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!description.trim() || submitting}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2 text-body-strong text-primary-foreground transition-all hover:bg-primary/90 disabled:opacity-50"
          >
            <span>{submitting ? "Sending..." : "Submit"}</span>
            {!submitting && <LuArrowRight className="size-3.5" />}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
